import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Popover,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { Add, Close, ContentCopy, Delete, Preview, UploadFile } from "@mui/icons-material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazySearchBranchQuery } from "store/services/branchApi";
import defaultLogoSrc from "assets/TMS-NepalCan-Logo-Quotation.png";
import { generateVendorQuotationPdf } from "utils/GenerateVendorQuotationPdf";
import { useDOUploadFile } from "hooks";

// ─── Default Data ──────────────────────────────────────────────────────────────

const EMPTY_GROUP = {
    description: "",
    from: null,
    remarks: "",
    rows: [{ to: null, ratesPerKg: "" }],
};

const INITIAL_STATE = {
    account: null,
    entity: null,
    title: "",
    isActive: true,
    logoFile: null,

    headerCompanyName: "Nepal Can Logistics Pvt. Ltd.",
    headerFormerName: "(Formerly known as Nepal Can Transport Pvt. Ltd.)",
    headerRegnNo: "Regd no. 306615/079/080",
    headerPanNo: "PAN no. 610442580",

    date: "",
    toName: "",
    toAddress: "",
    subject: "Service Quotation",

    tableGroups: [{ ...EMPTY_GROUP }],

    chargesNote: "*Charges are commercially confidential.",

    termsAndConditions: [
        "Quoted rates excluding 13% VAT.",
        "The Consignor is solely responsible for insuring the consignment to cover all risks of loss or damage, as the transport company will not be liable for such claims.",
        "The Consignor must ensure full compliance with all government/customs rules. The transport company is not responsible for goods declared contraband or subsequently confiscated by the Government.",
        "This quotation is valid for a period of 7 days only",
        "Rates remain stable within a Rs±10 fluctuation in fuel prices; any variation beyond this range may lead to revision upon mutual agreement.",
    ],

    closingNote: "Please feel free to write to us if you have any further queries about it.",
    thankYouNote: "Thank You.",
    regards: "With Regards,",
    signatoryName: "",
    signatoryDesignation: "",
    signatoryCompany: "Nepal Can Logistics Pvt. Ltd.",
    signatoryAddress: "Jadibuti, Kathmandu, Nepal",
    signatoryPhone: "",
    signatoryEmail: "",

    footerLeft: "Muni Bhairab Marga, Tinkune,\nKMC-32, Kathmandu",
    footerCenter: "01-5970736",
    footerRightWeb: "www.logistics.nepalcan.com",
    footerRightEmail: "care@logistics.nepalcan.com",
};

// ─── Helper components ─────────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
    <Grid item xs={12} sx={{ mt: 1 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle1" fontWeight={700} color="primary">
            {children}
        </Typography>
    </Grid>
);

// ─── BranchSearchAutocomplete ──────────────────────────────────────────────────

const BranchSearchAutocomplete = ({ value, onChange, account, entity, label = "Branch", ...rest }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const debounceRef = useRef(null);

    const [searchBranch, { isFetching }] = useLazySearchBranchQuery();

    useEffect(() => {
        if (value?.name && value?.code) {
            setInputValue(`${value.name} - ${value.code}`);
        } else {
            setInputValue("");
        }
    }, [value]);

    const handleInputChange = useCallback((_, newInputValue, reason) => {
        if (reason === "reset") return;

        setInputValue(newInputValue);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!newInputValue.trim() || !account || !entity) {
            setOptions([]);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            try {
                const result = await searchBranch({
                    q: newInputValue.trim(),
                    account,
                    entity,
                }).unwrap();
                setOptions(result || []);
            } catch {
                setOptions([]);
            }
        }, 300);
    }, [account, entity, searchBranch]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    return (
        <BaseAutocomplete
            fullWidth
            size="small"
            label={label}
            value={value ?? null}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={(_, data) => {
                onChange(data);
                if (data) setInputValue(`${data.name} - ${data.code}`);
                else setInputValue("");
            }}
            getOptionLabel={(o) => (o ? `${o.name} - ${o.code}` : "")}
            isOptionEqualToValue={(o, v) => o?._id === v?._id}
            options={options}
            loading={isFetching}
            loadingText="Searching branches..."
            noOptionsText={inputValue.trim() ? "No branches found" : "Type to search"}
            filterOptions={(x) => x}
            {...rest}
        />
    );
};

// ─── CopyGroupPopover ──────────────────────────────────────────────────────────

const CopyGroupPopover = ({ anchorEl, onClose, groupCount, onConfirm }) => {
    const [sourceIdx, setSourceIdx] = useState(0);
    const open = Boolean(anchorEl);

    useEffect(() => {
        if (open) setSourceIdx(0);
    }, [open]);

    const handleConfirm = () => {
        onConfirm(sourceIdx);
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { p: 2, minWidth: 220 } }}
        >
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Copy branches from…
            </Typography>
            <Select
                size="small"
                fullWidth
                value={sourceIdx}
                onChange={(e) => setSourceIdx(e.target.value)}
                sx={{ mb: 1.5 }}
            >
                {Array.from({ length: groupCount }, (_, i) => (
                    <MenuItem key={i} value={i}>
                        Group {i + 1}
                    </MenuItem>
                ))}
            </Select>
            <Box display="flex" gap={1} justifyContent="flex-end">
                <Button size="small" onClick={onClose}>
                    Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleConfirm}>
                    Add Group
                </Button>
            </Box>
        </Popover>
    );
};

// ─── Main Form ─────────────────────────────────────────────────────────────────

const VendorQuotationForm = ({
    isBusy = false,
    vendorQuotationDetail = null,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();
    const logoInputRef = useRef(null);
    const isMounted = useRef(true);
    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

    const [logoPreview, setLogoPreview] = useState(defaultLogoSrc);
    // Tracks the persisted logo attachment from DB. Separate from form fields
    // because it doesn't participate in validation and is forwarded as-is on submit
    // when no new file is selected.
    const [logoAttachment, setLogoAttachment] = useState(null);

    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [copyPopoverAnchor, setCopyPopoverAnchor] = useState(null);

    // Preview state
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewGenerating, setIsPreviewGenerating] = useState(false);

    // ── API Queries ──
    const { data: accounts, isLoading: loadingAccounts } =
        useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    // ── Form setup ──
    const {
        control,
        handleSubmit,
        setValue,
        reset,
        watch,
        getValues,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || null,
            account: viewEntity?.entity?.account || null,
        },
        mode: "onChange",
    });

    const _watchAccount = useWatch({ control, name: "account" });
    const _watchEntity = useWatch({ control, name: "entity" });
    const tableGroups = watch("tableGroups");
    const termsAndConditions = watch("termsAndConditions");

    const accountId = _watchAccount?._id ?? viewEntity?.entity?.account?._id;
    const entityId = _watchEntity?._id ?? viewEntity?.entity?._id;

    // ── Effects ──
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (!vendorQuotationDetail) {
            reset({
                ...INITIAL_STATE,
                entity: viewEntity?.entity || null,
                account: viewEntity?.entity?.account || null,
            });
            setLogoPreview(defaultLogoSrc);
            setLogoAttachment(null);
            return;
        }

        reset({
            ...INITIAL_STATE,
            ...vendorQuotationDetail,
            logoFile: null,
            account: vendorQuotationDetail.account || null,
            entity: vendorQuotationDetail.entity || null,
            date: vendorQuotationDetail.date || "",
            tableGroups:
                vendorQuotationDetail.tableGroups?.length
                    ? vendorQuotationDetail.tableGroups
                    : [{ ...EMPTY_GROUP }],
            termsAndConditions:
                vendorQuotationDetail.termsAndConditions?.length
                    ? vendorQuotationDetail.termsAndConditions
                    : INITIAL_STATE.termsAndConditions,
        });

        // Restore logo preview from DB attachment if present
        if (vendorQuotationDetail.logo?.url) {
            setLogoPreview(vendorQuotationDetail.logo.url);
            setLogoAttachment(vendorQuotationDetail.logo);
        } else {
            setLogoPreview(defaultLogoSrc);
            setLogoAttachment(null);
        }
    }, [vendorQuotationDetail, reset, viewEntity]);

    useEffect(() => {
        if (
            (isAccountAdmin || isSystemAdmin) &&
            _watchAccount?._id &&
            isMounted.current
        ) {
            fetchEntities({ account: _watchAccount._id });
        }
    }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    // ── Logo handlers ──
    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setValue("logoFile", file);
        setLogoPreview(URL.createObjectURL(file));
        // Clear the stored attachment so the new file takes priority on submit
        setLogoAttachment(null);
    };

    const handleLogoReset = () => {
        setValue("logoFile", null);
        setLogoPreview(defaultLogoSrc);
        setLogoAttachment(null);
        if (logoInputRef.current) logoInputRef.current.value = "";
    };

    // ── Preview handler ────────────────────────────────────────────────────────
    const handlePreview = async () => {
        setIsPreviewGenerating(true);
        try {
            const currentValues = getValues();
            const pdfDoc = await generateVendorQuotationPdf({
                ...currentValues,
                logo: logoAttachment,
            });
            // Revoke any previous blob URL before creating a new one
            if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
            const blobUrl = pdfDoc.output("bloburl");
            setPreviewBlobUrl(blobUrl);
            setIsPreviewOpen(true);
        } catch (err) {
            console.error("Preview generation error:", err);
        } finally {
            setIsPreviewGenerating(false);
        }
    };

    const handlePreviewClose = () => {
        setIsPreviewOpen(false);
        if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
        }
    };

    // ── Table group helpers ──

    const addGroup = useCallback(() => {
        setValue("tableGroups", [
            ...tableGroups,
            { ...EMPTY_GROUP, rows: [{ to: null, ratesPerKg: "" }] },
        ]);
    }, [tableGroups, setValue]);

    const addGroupCopiedFrom = useCallback((sourceIdx) => {
        const source = tableGroups[sourceIdx];
        const copiedRows = source.rows.map((row) => ({
            to: row.to ?? null,
            ratesPerKg: "",
        }));
        setValue("tableGroups", [
            ...tableGroups,
            {
                description: "",
                from: source.from ?? null,
                remarks: "",
                rows: copiedRows,
            },
        ]);
    }, [tableGroups, setValue]);

    const removeGroup = useCallback((gIdx) =>
        setValue("tableGroups", tableGroups.filter((_, i) => i !== gIdx)),
        [tableGroups, setValue]
    );

    const updateGroup = useCallback((gIdx, field, value) =>
        setValue(
            "tableGroups",
            tableGroups.map((g, i) => (i === gIdx ? { ...g, [field]: value } : g))
        ),
        [tableGroups, setValue]
    );

    const addRow = useCallback((gIdx) =>
        setValue(
            "tableGroups",
            tableGroups.map((g, i) =>
                i === gIdx
                    ? { ...g, rows: [...g.rows, { to: null, ratesPerKg: "" }] }
                    : g
            )
        ),
        [tableGroups, setValue]
    );

    const removeRow = useCallback((gIdx, rIdx) =>
        setValue(
            "tableGroups",
            tableGroups.map((g, i) =>
                i === gIdx
                    ? { ...g, rows: g.rows.filter((_, j) => j !== rIdx) }
                    : g
            )
        ),
        [tableGroups, setValue]
    );

    const updateRow = useCallback((gIdx, rIdx, field, value) =>
        setValue(
            "tableGroups",
            tableGroups.map((g, i) =>
                i === gIdx
                    ? {
                        ...g,
                        rows: g.rows.map((r, j) =>
                            j === rIdx ? { ...r, [field]: value } : r
                        ),
                    }
                    : g
            )
        ),
        [tableGroups, setValue]
    );

    // ── Terms helpers ──
    const addTerm = () =>
        setValue("termsAndConditions", [...termsAndConditions, ""]);

    const removeTerm = (idx) =>
        setValue("termsAndConditions", termsAndConditions.filter((_, i) => i !== idx));

    const updateTerm = (idx, value) =>
        setValue("termsAndConditions", termsAndConditions.map((t, i) => (i === idx ? value : t)));

    // ── Submit ──
    const handleOnSubmit = async (data) => {
        setIsGeneratingPdf(true);
        try {
            // Step 1: Upload logo if a new file was selected
            let resolvedLogoAttachment = logoAttachment; // may be existing DB attachment or null
            if (data.logoFile instanceof File) {
                const { uploadedFiles: logoFiles } = await handleUploadFiles(
                    [data.logoFile],
                    {
                        folderName: "VendorQuotation",
                        entity: viewEntity?.entity?._id,
                    }
                );
                resolvedLogoAttachment = logoFiles[0] ?? null;
            }

            // Step 2: Generate PDF (uses new file or stored URL or default)
            const pdfDoc = await generateVendorQuotationPdf({
                ...data,
                logo: resolvedLogoAttachment,
            });
            const pdfFileName = `${data.title || "quotation"}-${data.date || Date.now()}.pdf`;
            const pdfBlob = pdfDoc.output("blob");
            const pdfFile = new File([pdfBlob], pdfFileName, { type: "application/pdf" });

            // Step 3: Upload PDF
            const { uploadedFiles } = await handleUploadFiles([pdfFile], {
                folderName: "VendorQuotation",
                entity: viewEntity?.entity?._id,
            });

            const sanitizedTableGroups = data.tableGroups.map((group) => ({
                ...group,
                from: group.from?._id ?? null,
                rows: group.rows.map((row) => ({
                    ...row,
                    to: row.to?._id ?? null,
                })),
            }));

            onSubmit({
                ...data,
                logoFile: undefined,
                logo: resolvedLogoAttachment,
                account: data?.account?._id,
                entity: data?.entity?._id,
                tableGroups: sanitizedTableGroups,
                pdfAttachment: uploadedFiles[0],
            });
        } catch (err) {
            console.error("PDF generation/upload error:", err);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const isSubmitting = isBusy || isGeneratingPdf || uploadingFiles;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <AppForm loading={isSubmitting}>
                <AppForm.Content>
                    <Grid container spacing={3}>

                        {/* ── Account & Entity ──────────────────────────────────────── */}
                        {(isAccountAdmin || isSystemAdmin) && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="account"
                                        control={control}
                                        rules={{ required: "Please select an account." }}
                                        render={({ field }) => (
                                            <BaseAutocomplete
                                                {...field}
                                                fullWidth required
                                                label="Account"
                                                value={field.value ?? null}
                                                onChange={(_, data) => {
                                                    field.onChange(data);
                                                    setValue("entity", null);
                                                }}
                                                getOptionLabel={(o) => o?.name ?? ""}
                                                isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                                options={accounts || []}
                                                loading={loadingAccounts}
                                                loadingText="Loading Account..."
                                                disabled={isAccountAdmin}
                                                helperText={errors?.account?.message}
                                                error={!!errors?.account}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="entity"
                                        control={control}
                                        rules={{ required: "Please select an entity." }}
                                        render={({ field: { value, onChange } }) => (
                                            <BaseAutocomplete
                                                fullWidth required
                                                label="Entity"
                                                value={value ?? null}
                                                onChange={(_, data) => onChange(data)}
                                                getOptionLabel={(o) => o?.name ?? ""}
                                                isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                                options={entities || []}
                                                loading={loadingEntities}
                                                loadingText="Loading Entities..."
                                                helperText={errors?.entity?.message}
                                                error={!!errors?.entity}
                                            />
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* ── Title ─────────────────────────────────────────────────── */}
                        <Grid item xs={12}>
                            <Controller
                                name="title"
                                control={control}
                                rules={{ required: "Title is required." }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth required
                                        size="small"
                                        label="Title"
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {/* ── PDF Header ─────────────────────────────────────────────── */}
                        <SectionHeading>PDF Header</SectionHeading>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Logo
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Box
                                    component="img"
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    sx={{
                                        height: 64,
                                        maxWidth: 180,
                                        objectFit: "contain",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        p: 0.5,
                                        background: "#fff",
                                    }}
                                />
                                <Box display="flex" gap={1}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={logoInputRef}
                                        style={{ display: "none" }}
                                        onChange={handleLogoSelect}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<UploadFile />}
                                        onClick={() => logoInputRef.current?.click()}
                                    >
                                        Change Logo
                                    </Button>
                                    <Button size="small" color="error" onClick={handleLogoReset}>
                                        Reset to Default
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller name="headerCompanyName" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Company Name" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="headerFormerName" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Formerly Known As" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="headerRegnNo" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Regd No." />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="headerPanNo" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="PAN No." />
                                )}
                            />
                        </Grid>

                        {/* ── Quotation Details ─────────────────────────────────────── */}
                        <SectionHeading>Quotation Details</SectionHeading>

                        <Grid item xs={12} md={4}>
                            <Controller
                                name="date"
                                control={control}
                                rules={{ required: "Date is required." }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth required
                                        type="date"
                                        size="small"
                                        label="Date"
                                        InputLabelProps={{ shrink: true }}
                                        error={!!errors.date}
                                        helperText={errors.date?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Controller name="subject" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Subject" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="toName" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="To — Name" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="toAddress" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="To — Address" />
                                )}
                            />
                        </Grid>

                        {/* ── Rate Table ────────────────────────────────────────────── */}
                        <SectionHeading>Rate Table</SectionHeading>

                        {tableGroups?.map((group, gIdx) => (
                            <Grid item xs={12} key={gIdx}>
                                <Paper
                                    variant="outlined"
                                    sx={{ p: 2, borderRadius: 2, position: "relative" }}
                                >
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={2}
                                    >
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            Group {gIdx + 1}
                                        </Typography>
                                        {tableGroups.length > 1 && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeGroup(gIdx)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Grid container spacing={2} mb={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth size="small"
                                                label="Description"
                                                value={group.description}
                                                onChange={(e) =>
                                                    updateGroup(gIdx, "description", e.target.value)
                                                }
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <BranchSearchAutocomplete
                                                label="From"
                                                value={group.from ?? null}
                                                onChange={(data) => updateGroup(gIdx, "from", data)}
                                                account={accountId}
                                                entity={entityId}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={5}>
                                            <TextField
                                                fullWidth size="small"
                                                label="Remarks"
                                                value={group.remarks}
                                                onChange={(e) =>
                                                    updateGroup(gIdx, "remarks", e.target.value)
                                                }
                                            />
                                        </Grid>
                                    </Grid>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={1}
                                    >
                                        Destinations
                                    </Typography>

                                    {group.rows?.map((row, rIdx) => (
                                        <Box
                                            key={rIdx}
                                            display="flex"
                                            alignItems="flex-start"
                                            gap={1}
                                            mb={1}
                                        >
                                            <BranchSearchAutocomplete
                                                label="To"
                                                value={row.to ?? null}
                                                onChange={(data) => updateRow(gIdx, rIdx, "to", data)}
                                                account={accountId}
                                                entity={entityId}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Rates/Kg"
                                                sx={{ width: 120, flexShrink: 0 }}
                                                value={row.ratesPerKg}
                                                onChange={(e) =>
                                                    updateRow(gIdx, rIdx, "ratesPerKg", e.target.value)
                                                }
                                            />
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeRow(gIdx, rIdx)}
                                                disabled={group.rows.length === 1}
                                                sx={{ mt: 0.5 }}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    <Button
                                        size="small"
                                        startIcon={<Add />}
                                        onClick={() => addRow(gIdx)}
                                        sx={{ mt: 0.5 }}
                                    >
                                        Add Row
                                    </Button>
                                </Paper>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={addGroup}
                                >
                                    Add Group
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopy />}
                                    onClick={(e) => setCopyPopoverAnchor(e.currentTarget)}
                                >
                                    Add Group (Copy Branches)
                                </Button>
                            </Box>

                            <CopyGroupPopover
                                anchorEl={copyPopoverAnchor}
                                onClose={() => setCopyPopoverAnchor(null)}
                                groupCount={tableGroups.length}
                                onConfirm={addGroupCopiedFrom}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller name="chargesNote" control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth size="small"
                                        label="Charges Note"
                                        helperText="Appears below the table with a yellow highlight"
                                    />
                                )}
                            />
                        </Grid>

                        {/* ── Terms & Conditions ────────────────────────────────────── */}
                        <SectionHeading>Terms &amp; Conditions</SectionHeading>

                        {termsAndConditions?.map((term, idx) => (
                            <Grid item xs={12} key={idx}>
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                    <Typography variant="body2" sx={{ mt: 1, minWidth: 22, fontWeight: 600 }}>
                                        {idx + 1}.
                                    </Typography>
                                    <TextField
                                        fullWidth size="small" multiline
                                        label={`Term ${idx + 1}`}
                                        value={term}
                                        onChange={(e) => updateTerm(idx, e.target.value)}
                                    />
                                    <IconButton
                                        size="small" color="error"
                                        onClick={() => removeTerm(idx)}
                                        disabled={termsAndConditions.length === 1}
                                        sx={{ mt: 0.5 }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Button size="small" startIcon={<Add />} onClick={addTerm}>
                                Add Term
                            </Button>
                        </Grid>

                        {/* ── Closing & Signatory ───────────────────────────────────── */}
                        <SectionHeading>Closing &amp; Signatory</SectionHeading>

                        <Grid item xs={12}>
                            <Controller name="closingNote" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Closing Note" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="thankYouNote" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Thank You Text" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="regards" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Regards Text" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryName" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Signatory Name" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryDesignation" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Designation" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryCompany" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Company" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryAddress" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Address" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryPhone" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Phone" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="signatoryEmail" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Email" />
                                )}
                            />
                        </Grid>

                        {/* ── PDF Footer ────────────────────────────────────────────── */}
                        <SectionHeading>PDF Footer</SectionHeading>

                        <Grid item xs={12} md={6}>
                            <Controller name="footerLeft" control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth size="small" multiline rows={2}
                                        label="Footer Left"
                                        helperText={`Use \\n for a new line (e.g. "Line 1\\nLine 2")`}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="footerCenter" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Footer Center (Phone)" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="footerRightWeb" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Footer Right — Website" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller name="footerRightEmail" control={control}
                                render={({ field }) => (
                                    <TextField {...field} fullWidth size="small" label="Footer Right — Email" />
                                )}
                            />
                        </Grid>

                        {/* Active toggle (edit mode only) */}
                        {vendorQuotationDetail && (
                            <Grid item xs={6}>
                                <Controller
                                    name="isActive"
                                    control={control}
                                    render={({ field }) => (
                                        <AppSwitch {...field} label="Is Active?" />
                                    )}
                                />
                            </Grid>
                        )}
                    </Grid>
                </AppForm.Content>

                <AppForm.Actions>
                    <Button onClick={() => navigate(-1)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Preview />}
                        onClick={handlePreview}
                        disabled={isSubmitting || isPreviewGenerating}
                    >
                        {isPreviewGenerating ? "Generating…" : "Preview"}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(handleOnSubmit)}
                        disabled={isSubmitting}
                    >
                        {isGeneratingPdf
                            ? "Generating PDF…"
                            : isBusy
                                ? "Saving…"
                                : vendorQuotationDetail
                                    ? "Update Vendor Quotation"
                                    : "Add Vendor Quotation"}
                    </Button>
                </AppForm.Actions>
            </AppForm>

            {/* ── PDF Preview Dialog ─────────────────────────────────────────── */}
            <Dialog
                open={isPreviewOpen}
                onClose={handlePreviewClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: "90vh" } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        PDF Preview
                    </Typography>
                    <IconButton size="small" onClick={handlePreviewClose}>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: "hidden" }}>
                    {previewBlobUrl && (
                        <iframe
                            src={previewBlobUrl}
                            title="Vendor Quotation Preview"
                            width="100%"
                            height="100%"
                            style={{ border: "none", display: "block" }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VendorQuotationForm;